import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { useCriarLancamento } from "@/hooks/use-equipe";
import { hojeNaFazenda } from "@/lib/data-fazenda";
import { paraCentavos, validarLancamento, ehMensalista, formatarBRL } from "@shared/equipe-fechamento";
import { msgDoErro } from "./msg-do-erro";

/**
 * "+ Lançamento" da aba "Mensalistas · mês" (Financeiro v3, §4.5): ADICIONAL
 * (+) ou DESCONTO (−), com a data LIMITADA ao intervalo da competência em
 * exibição (o client valida via `competenciaAlvo`; o servidor decide a quinzena).
 *
 * Onde liquida (D-M4): ADICIONAL sai na quinzena que cobre a data (precisa
 * estar ABERTA — I11); DESCONTO de mensalista abate SÓ no fechamento do mês
 * (aceita quinzena fechada; trava só com o fechamento FECHADO — D-M5).
 */
export function LancamentoSheet({
  open, onOpenChange, funcionario, competencia, inicio, fim,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  funcionario: { id: string; nome: string; apelido: string | null; tipo_remuneracao: string } | null;
  competencia: string;
  inicio: string;
  fim: string;
}) {
  const isMobile = useIsMobile();
  const criar = useCriarLancamento();
  const { toast } = useToast();

  const [sinal, setSinal] = useState<"ADICIONAL" | "DESCONTO">("DESCONTO");
  const [data, setData] = useState(inicio);
  const [descricao, setDescricao] = useState("");
  const [valor, setValor] = useState("");

  useEffect(() => {
    if (!open) return;
    // Default = hoje na fazenda, CLAMPADO ao mês em exibição (nunca
    // `toISOString()`: das 21h às 23h59 na Bahia isso devolve amanhã).
    const hoje = hojeNaFazenda();
    setData(hoje < inicio ? inicio : hoje > fim ? fim : hoje);
    setSinal("DESCONTO");
    setDescricao("");
    setValor("");
  }, [open, inicio, fim]);

  const erro = validarLancamento({ sinal, valor, descricao });
  const foraDoMes = data < inicio || data > fim;
  const mensalista = funcionario ? ehMensalista({ tipoRemuneracao: funcionario.tipo_remuneracao }) : false;

  const salvar = async () => {
    if (!funcionario) return;
    try {
      const r = await criar.mutateAsync({
        funcionarioId: funcionario.id, data, sinal, descricao: descricao.trim(), valor, competenciaAlvo: competencia,
      });
      if (r.aviso === "FECHAMENTO_JA_GERADO") {
        toast({
          title: "Lançado — mas o fechamento deste mês já foi gerado.",
          description: "O campo Descontos do fechamento não muda sozinho: ajuste-o na linha da pessoa.",
        });
      } else {
        toast({
          title: sinal === "DESCONTO"
            ? (mensalista ? "Desconto lançado — abate no fechamento do mês." : "Desconto lançado — abate na quinzena.")
            : "Adicional lançado — sai na quinzena que cobre a data.",
        });
      }
      onOpenChange(false);
    } catch (e) {
      toast({ title: msgDoErro(e, "Erro ao lançar"), variant: "destructive" });
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side={isMobile ? "bottom" : "right"} className={isMobile ? "rounded-t-2xl max-h-[92vh] overflow-y-auto" : "overflow-y-auto"}>
        <SheetHeader>
          <SheetTitle>Lançamento · {funcionario?.apelido ?? funcionario?.nome}</SheetTitle>
          <SheetDescription>
            {mensalista
              ? "Adicional sai na quinzena da data; desconto abate só no fechamento do mês."
              : "Adicional e desconto saem na quinzena que cobre a data."}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-3 py-4">
          <div className="grid grid-cols-2 gap-2">
            <Button
              type="button"
              variant={sinal === "ADICIONAL" ? "default" : "outline"}
              className="min-h-11"
              onClick={() => setSinal("ADICIONAL")}
              data-testid="lancamento-sinal-adicional"
            >
              + Adicional
            </Button>
            <Button
              type="button"
              variant={sinal === "DESCONTO" ? "default" : "outline"}
              className="min-h-11"
              onClick={() => setSinal("DESCONTO")}
              data-testid="lancamento-sinal-desconto"
            >
              − Desconto
            </Button>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Data (dentro de {competencia})</Label>
            <Input type="date" min={inicio} max={fim} value={data} onChange={e => setData(e.target.value)} className="min-h-11" />
            {foraDoMes && <p className="text-[11px] text-erro">A data precisa estar entre {inicio} e {fim}.</p>}
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Descrição</Label>
            <Input
              value={descricao}
              onChange={e => setDescricao(e.target.value)}
              placeholder={sinal === "DESCONTO" ? "falta 03/09" : "colheita extra"}
              className="min-h-11"
            />
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Valor</Label>
            <Input type="number" step="0.01" inputMode="decimal" value={valor} onChange={e => setValor(e.target.value)} className="min-h-11" placeholder="0,00" />
          </div>

          {paraCentavos(valor) > 0 && (
            <p className="text-sm">
              {sinal === "DESCONTO" ? "− " : "+ "}<strong>{formatarBRL(valor)}</strong>
              <span className="text-muted-foreground text-xs">
                {" · "}{sinal === "DESCONTO" && mensalista ? "abate no fechamento do mês" : "sai na quinzena da data"}
              </span>
            </p>
          )}

          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1 min-h-11" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button className="flex-1 min-h-11" onClick={salvar} disabled={criar.isPending || !!erro || foraDoMes || !funcionario}>
              {criar.isPending ? "Lançando..." : "Lançar"}
            </Button>
          </div>
          {erro && (descricao || valor) && <p className="text-[11px] text-erro">{erro}</p>}
        </div>
      </SheetContent>
    </Sheet>
  );
}
